import { useEffect, useMemo, useRef } from 'react';
import { useLocalParticipant } from '@daily-co/daily-react-hooks';
import { Box } from '@mui/material';
import PropTypes from 'prop-types';
import { useLocalStorage } from 'usehooks-ts';
import { useSettings } from '@juno/utils';
import { useSessionContext } from '@juno/utils';
import { CallStatusLocalStorageValues } from '../../constants';
import { useCallState } from '../../contexts/CallProvider';
import { useCallUI } from '../../hooks/useCallUI';
import Room from '../Room';
import { Asides } from './Asides';
import { Modals } from './Modals';

export const App = ({ customComponentForState, leaveCall, sessionData, withAudio, withVideo }) => {
  const { roomExp, state, roomInfo, setRoomInfo, leave, callObject } = useCallState();
  const { showSideMenu, showMessenger, isSmallScreen, user } = useSettings();
  const { setCallState } = useSessionContext();
  const [callClosedValue, setcallClosedValue] = useLocalStorage(
    CallStatusLocalStorageValues.declinedCall,
    { value: false },
  );
  const localParticipant = useLocalParticipant();
  const hasAutoTurnedOnAudioVideo = useRef(withAudio || withVideo ? false : true);
  const componentForState = useCallUI({
    state,
    room: <Room />,
    callEnded: leaveCall,
    ...customComponentForState,
  });

  const checkforCallDeclinedState = () => {
    if (callClosedValue.value) {
      setCallState((old) => ({
        isCaller: true,
        dialogOpen: true,
        dialogStatus: JSON.parse(localStorage.getItem(CallStatusLocalStorageValues.callStatus))
          .value,
        otherUser: {
          id: JSON.parse(localStorage.getItem(CallStatusLocalStorageValues.otherUserId)).value,
          avatar: '',
          first_name: JSON.parse(
            localStorage.getItem(CallStatusLocalStorageValues.otherUserFirstName),
          ).value,
          last_name: JSON.parse(
            localStorage.getItem(CallStatusLocalStorageValues.otherUserLastName),
          ).value,
        },
      }));
      leave();
    }
  };

  useEffect(() => {
    checkforCallDeclinedState();
  }, [callClosedValue]);

  useEffect(() => {
    setRoomInfo && setRoomInfo({ ...roomInfo, session: sessionData });
  }, [sessionData]);

  useEffect(() => {
    if (callObject) {
      if (hasAutoTurnedOnAudioVideo.current) return;
      if (withAudio || withVideo) {
        joinWithAudioVideo();
      }
      hasAutoTurnedOnAudioVideo.current = true;
    }
  }, [callObject]);

  const joinWithAudioVideo = async () => {
    try {
      const userData = localParticipant?.userData ?? {
        userId: user?.id,
        userAvatar: user?.icon || user?.avatar || '',
      };
      await callObject?.setUserData({ ...userData, onlyWatching: false });
      await callObject?.join();
      callObject.setLocalAudio(withAudio);
      callObject.setLocalVideo(withVideo);
    } catch (e) {
      console.error(e);
      checkforCallDeclinedState();
    }
  };

  useEffect(() => {
    return () => {
      leave();
    };
  }, []);

  const isContained = showSideMenu || showMessenger;

  // Memoize children to avoid unnecessary renders from HOC
  return useMemo(
    () => (
      <>
        {
          <Box
            className='app'
            p={'5px'}
            display={'flex'}
            justifyContent={'center'}
            alignItems={'center'}
            position={'relative'}
            sx={{
              bgcolor: '#262626',
              height: (theme) =>
                isContained
                  ? isSmallScreen
                    ? 'calc(100vh - 120px)'
                    : `calc(100vh - 60px - ${theme.spacing(4)})`
                  : isSmallScreen
                  ? 'calc(100vh - 120px)'
                  : 'calc(100vh - 60px)',
            }}
          >
            {componentForState()}
            <Modals />
            <Asides />
          </Box>
        }
      </>
    ),
    [componentForState, roomExp, isContained],
  );
};

App.propTypes = {
  customComponentForState: PropTypes.any,
  leaveCall: PropTypes.func,
  sessionData: PropTypes.object,
};

export default App;
